import React, { useEffect } from "react";
import { LEDBarConfig } from "./AgentsStore";
import { Box, Text } from "grommet";
import { ColorPicker } from "../components/ThemeBuilder";
import Color from "colorjs.io";
import styled, { keyframes } from "styled-components";

const Solid = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    width: 100%;
    height: 44px;
    border-radius: 6px;
    content: "";
    background: ${(props) => props.color};
`;

const AnimatedRainbow = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    width: 100%;
    height: 44px;
    border-radius: 6px;
    content: "";
    background: linear-gradient(90deg, #ff0000, #ff7f00, #ffff00, #00ff00, #0000ff, #8b00ff, #ff00ff, #ff0000);
    background-size: 300% 300%;
    animation: rainbow 5s linear infinite;
    @keyframes rainbow {
        0% {
            background-position: 300% 50%;
        }
        1000% {
            background-position: 0% 50%;
        }
    }
`;

const AnimatedWaveRainbow = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    width: 100%;
    height: 44px;
    border-radius: 6px;
    content: "";
    background: linear-gradient(90deg, transparent 0%, #ff0000 1%, #ff7f00 2%, #ffff00 3%, #00ff00 4%, #0000ff 5%, #8b00ff 6%, #ff00ff 7%, transparent 8%);
    background-size: 300% 300%;
    animation: wave 3s linear infinite;
    @keyframes wave {
        0% {
            background-position: 300% 50%;
        }
        1000% {
            background-position: 0% 50%;
        }
    }
`;

const flashing = (color) => keyframes`
        0% {
            background: ${color};
        }
        49% {
            background: ${color};
        }
        50% {
            background: transparent;
        }
        100% {
            background: transparent;
        }
`;

const FlashingColor = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    width: 100%;
    height: 44px;
    border-radius: 6px;
    content: "";
    background: ${(props) => props.color};
    animation: ${(props) => flashing(props.color)} 1s infinite;
`;

const fading = (color) => keyframes`
        0% {
            background: ${color};
        }
        50% {
            background: transparent;
        }
        100% {
            background: ${color};
        }
    `;

const FadingColor = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    width: 100%;
    height: 44px;
    border-radius: 6px;
    content: "";
    background: ${(props) => props.color};
    animation: ${(props) => fading(props.color)} 2s infinite;
`;

const getModeString = (mode) => {
    switch (mode) {
        case 0:
            return "Pause";
        case 1:
            return "Solid Color";
        case 2:
            return "Rainbow";
        case 3:
            return "Wave Rainbow";
        case 4:
            return "Flashing Color";
        case 5:
            return "Fading Color";
    }
};

const getContrast = (color) => {
    try {
        return Color.contrast("black", color, "APCA").toString();
    } catch (e) {}
    return color;
};

export const RenderLEDBarAction = ({ name, config, style }: { name: string; config: LEDBarConfig; style?: any }) => {
    const [color, setColor] = React.useState("#ffffff");

    useEffect(() => {
        setColor(new Color("hsv", [(config.colorH / 255) * 360, (config.colorS / 255) * 100, (config.colorV / 255) * 100]).to("srgb").clone().toString());
    }, [name, config]);

    return (
        <>
            {config.mode == 1 && (
                <Solid color={color} style={style}>
                    <Text color={getContrast(color)}>{name}</Text>
                </Solid>
            )}
            {config.mode == 2 && (
                <AnimatedRainbow style={style}>
                    <Text color={getContrast(color)}>{name}</Text>
                </AnimatedRainbow>
            )}
            {config.mode == 3 && (
                <AnimatedWaveRainbow style={style}>
                    <Text color={getContrast(color)}>{name}</Text>
                </AnimatedWaveRainbow>
            )}
            {config.mode == 4 && (
                <FlashingColor color={color} style={style}>
                    <Text color={getContrast(color)}>{name}</Text>
                </FlashingColor>
            )}
            {config.mode == 5 && (
                <FadingColor color={color} style={style}>
                    <Text color={getContrast(color)}>{name}</Text>
                </FadingColor>
            )}
        </>
    );
};

const LEDBarAction = ({ onChange, config }: { config?: LEDBarConfig; onChange?: (config: LEDBarConfig) => void }) => {
    const [color, setColor] = React.useState("#ffffff");

    const [colorH, setColorH] = React.useState(config?.colorH || 0);
    const [colorS, setColorS] = React.useState(config?.colorS || 255);
    const [colorV, setColorV] = React.useState(config?.colorV || 255);

    const [mode, setMode] = React.useState(config?.mode || 0);

    const handleColorChange = (colorString) => {
        let color = new Color(colorString);
        let [h, s, v] = color.hsv;

        if (!h) h = 0;
        if (!s) s = 0;
        if (!v) v = 0;

        setColorH(Math.floor((h / 360) * 255));
        setColorS(Math.floor((s / 100) * 255));
        setColorV(Math.floor((v / 100) * 255));
        setColor(colorString);
    };

    useEffect(() => {
        if (onChange) {
            let v = colorV;
            let s = colorS;

            if (mode == 2 || mode == 3) {
                v = 255;
                s = 255;
            }
            onChange({ mode, colorH, colorS: s, colorV: v } as LEDBarConfig);
        }
    }, [mode, colorH, colorS, colorV]);

    return (
        <Box gap="xsmall">
            <Box direction="row" gap="small" align="center">
                <ColorPicker color={color} onChange={handleColorChange} />
                <Text>Selected Mode: {getModeString(mode)}</Text>
            </Box>
            <Box pad="small" border={{ color: color, size: "2px" }} round="6px" onClick={() => setMode(0)} align="center">
                <Text>Pause</Text>
            </Box>
            <Solid color={color} onClick={() => setMode(1)}></Solid>
            <AnimatedRainbow onClick={() => setMode(2)}></AnimatedRainbow>
            <AnimatedWaveRainbow onClick={() => setMode(3)}></AnimatedWaveRainbow>
            <FlashingColor color={color} onClick={() => setMode(4)}></FlashingColor>
            <FadingColor color={color} onClick={() => setMode(5)}></FadingColor>
        </Box>
    );
};

export default LEDBarAction;
